import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import { HoverHelp } from '@/tableControls'
import {
  addOverride,
  getMonthDetail,
  markReconciled,
  refreshMonth,
  removeOverride,
  removeOverrides,
  type MonthDetailResponse,
} from '@/services/bankRecon'
import { toast } from '@/store/toastStore'
import { formatApiError } from '@/utils/apiError'
import { formatLocalDateTime } from '@/utils/dateFormat'
import BankReconExceptionsTable from './BankReconExceptionsTable'
import BankReconLinker from './BankReconLinker'
import {
  applyLinkOverride,
  applyUnlinkOverrides,
  findQboTxn,
} from './bankReconLinkerHelpers'
import {
  emptyBankReconMonthDetail,
  formatBankReconTableAmount,
  isBankReconMissingUploadError,
} from './bankReconFormat'

type Props = {
  accountKey: string
  accountLabel: string
  reportMonth: string
  canEdit: boolean
  removingUpload?: boolean
  onRemoveUpload?: () => void
  onDetailChange: () => Promise<void>
}

type ReconcileStatusTone = 'success' | 'warning' | 'ready'

function ReconcileStatusCallout({
  tone,
  title,
  children,
}: {
  tone: ReconcileStatusTone
  title: string
  children?: ReactNode
}) {
  const toneClass =
    tone === 'success'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-950'
      : 'border-amber-200 bg-amber-50 text-amber-950'
  const iconClass = tone === 'success' ? 'text-emerald-600' : 'text-amber-600'
  const Icon =
    tone === 'success'
      ? CheckCircleIcon
      : tone === 'ready'
        ? InformationCircleIcon
        : ExclamationTriangleIcon

  return (
    <div
      className={`flex items-start gap-3 rounded-lg border px-4 py-3 ${toneClass}`}
      role="status"
    >
      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${iconClass}`} aria-hidden />
      <div className="min-w-0 flex-1 text-sm leading-relaxed">
        <p className="font-medium">{title}</p>
        {children ? <div className="mt-1 text-[0.8125rem] text-current/80">{children}</div> : null}
      </div>
    </div>
  )
}

export default function BankReconAccountPanel({
  accountKey,
  accountLabel,
  reportMonth,
  canEdit,
  removingUpload = false,
  onRemoveUpload,
  onDetailChange,
}: Props) {
  const [detail, setDetail] = useState<MonthDetailResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [reconcileNote, setReconcileNote] = useState('')
  const [markingReconciled, setMarkingReconciled] = useState(false)
  const linkRequestGenRef = useRef(0)

  const loadDetail = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getMonthDetail(accountKey, reportMonth)
      setDetail(data)
      setReconcileNote(data.reconcile_note ?? '')
    } catch (err) {
      if (isBankReconMissingUploadError(err)) {
        setDetail(emptyBankReconMonthDetail(accountKey, reportMonth))
        setReconcileNote('')
      } else {
        setDetail(null)
        toast.error(formatApiError(err, 'Failed to load bank recon detail'))
      }
    } finally {
      setLoading(false)
    }
  }, [accountKey, reportMonth])

  useEffect(() => {
    void loadDetail()
  }, [loadDetail])

  const refreshDetail = useCallback(
    async (nextDetail?: MonthDetailResponse) => {
      if (nextDetail) {
        setDetail(nextDetail)
        setReconcileNote(nextDetail.reconcile_note ?? '')
        void onDetailChange()
        return
      }
      await loadDetail()
      void onDetailChange()
    },
    [loadDetail, onDetailChange],
  )

  const handleRefresh = async () => {
    if (!canEdit) return
    setRefreshing(true)
    try {
      const next = await refreshMonth(accountKey, reportMonth)
      await refreshDetail(next)
      toast.success('Refreshed from QuickBooks')
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to refresh from QBO'))
    } finally {
      setRefreshing(false)
    }
  }

  const handleLink = async (payload: {
    statementLineKeys: string[]
    qboTxnIds: string[]
    note: string
  }) => {
    if (!detail) return
    const requestGen = ++linkRequestGenRef.current
    const previousDetail = detail
    const optimisticDetail = applyLinkOverride(detail, {
      statementLineKeys: payload.statementLineKeys,
      qboTxnIds: payload.qboTxnIds,
      note: payload.note,
    })
    setDetail(optimisticDetail)
    void onDetailChange()
    try {
      const response = await addOverride(accountKey, reportMonth, {
        kind: 'match',
        statement_line_keys: payload.statementLineKeys,
        qbo_txn_ids: payload.qboTxnIds.map((lineKey) => {
          const txn = findQboTxn(previousDetail, lineKey)
          return txn?.txn_id ?? lineKey.split('#')[0] ?? lineKey
        }),
        note: payload.note,
      })
      if (requestGen !== linkRequestGenRef.current) return
      setDetail(response.detail)
      setReconcileNote(response.detail.reconcile_note ?? '')
      void onDetailChange()
      toast.success('Match saved')
    } catch (err) {
      if (requestGen === linkRequestGenRef.current) {
        setDetail(previousDetail)
        void onDetailChange()
      }
      toast.error(formatApiError(err, 'Failed to save match'))
      throw err
    }
  }

  const handleIgnore = async (payload: {
    kind: 'ignore_qbo_txn'
    qboTxnId: string
    note: string
  }) => {
    try {
      const response = await addOverride(accountKey, reportMonth, {
        kind: payload.kind,
        qbo_txn_id: payload.qboTxnId,
        note: payload.note,
      })
      await refreshDetail(response.detail)
      toast.success('Ignore override saved')
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to save ignore'))
      throw err
    }
  }

  const handleUnlinkOverrides = async (overrideIds: number[]) => {
    if (overrideIds.length === 0 || !detail) return
    const previousDetail = detail
    const optimisticDetail = applyUnlinkOverrides(detail, overrideIds)
    setDetail(optimisticDetail)
    void onDetailChange()
    try {
      const response = await removeOverrides(accountKey, reportMonth, overrideIds)
      setDetail(response.detail)
      setReconcileNote(response.detail.reconcile_note ?? '')
      void onDetailChange()
      toast.success('Unlinked')
    } catch (err) {
      setDetail(previousDetail)
      void onDetailChange()
      toast.error(formatApiError(err, 'Failed to unlink'))
      throw err
    }
  }

  const handleRemoveOverride = async (overrideId: number) => {
    try {
      const response = await removeOverride(accountKey, reportMonth, overrideId)
      await refreshDetail(response.detail)
      toast.success('Override removed')
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to remove override'))
      throw err
    }
  }

  const handleMarkReconciled = async (reconciled: boolean) => {
    if (!canEdit) return
    const note = reconcileNote.trim()
    if (reconciled && (detail?.headlines.exception_count ?? 0) > 0 && !note) {
      toast.error('Enter a note to mark reconciled while exceptions remain.')
      return
    }
    setMarkingReconciled(true)
    try {
      const response = await markReconciled(accountKey, reportMonth, {
        reconciled,
        note: note || undefined,
      })
      await refreshDetail(response.detail)
      toast.success(reconciled ? 'Marked reconciled in BookKeeper' : 'Un-marked reconciled')
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to update reconciled status'))
    } finally {
      setMarkingReconciled(false)
    }
  }

  const statement = detail?.statement
  const headlines = detail?.headlines
  const isReconciled = Boolean(detail?.reconciled_at)
  const exceptionCount = headlines?.exception_count ?? 0
  const markDisabled =
    markingReconciled || (exceptionCount > 0 && !reconcileNote.trim() && !isReconciled)

  const headlineItems = useMemo(() => {
    if (!headlines) return []
    const items = [
      {
        label: 'Statement beginning',
        value: formatBankReconTableAmount(statement?.beginning_balance),
      },
      {
        label: 'Statement ending',
        value: formatBankReconTableAmount(headlines.statement_ending_balance),
      },
      {
        label: 'QBO cleared',
        value: formatBankReconTableAmount(headlines.qbo_cleared_balance),
      },
    ]
    if (detail?.qbo_status_available) {
      items.push(
        {
          label: 'Expected difference',
          value: formatBankReconTableAmount(headlines.expected_difference),
        },
        {
          label: 'Beginning-balance drift',
          value: formatBankReconTableAmount(headlines.beginning_balance_drift),
        },
      )
    }
    return items
  }, [detail?.qbo_status_available, headlines, statement?.beginning_balance])

  if (loading && !detail) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-gray-500">
        <ArrowPathIcon className="mr-2 h-4 w-4 animate-spin" />
        Loading {accountLabel}…
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {detail ? (
        <>
          {statement?.source_filename ? (
            <div className="flex items-center gap-1 text-sm text-gray-600">
              <span className="truncate" title={statement.source_filename}>
                {statement.source_filename}
              </span>
              {canEdit && onRemoveUpload ? (
                <button
                  type="button"
                  aria-label={`Remove ${statement.source_filename}`}
                  className="shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                  disabled={removingUpload}
                  onClick={() => onRemoveUpload()}
                >
                  <XMarkIcon className={`h-4 w-4 ${removingUpload ? 'animate-pulse' : ''}`} />
                </button>
              ) : null}
            </div>
          ) : null}
          <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-4">
              <h2 className="text-base font-semibold text-gray-900">Balances</h2>
              {canEdit ? (
                <HoverHelp
                  aria-label="Refresh from QBO"
                  label={
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={refreshing || !statement}
                      onClick={() => void handleRefresh()}
                    >
                      <ArrowPathIcon className={`mr-1.5 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                      Refresh from QBO
                    </Button>
                  }
                >
                  {detail.qbo_fetched_at
                    ? `QBO data fetched ${formatLocalDateTime(detail.qbo_fetched_at)}`
                    : 'QBO data has not been fetched for this month yet.'}
                </HoverHelp>
              ) : null}
            </div>
            <div className="px-5 py-4">
              <div className="flex flex-wrap items-stretch gap-6 text-sm">
                {headlineItems.map((item) => (
                  <div key={item.label} className="flex w-[150px] shrink-0 flex-col">
                    <div className="flex flex-1 flex-col justify-end">
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                        {item.label}
                      </p>
                    </div>
                    <p className="mt-0.5 font-semibold tabular-nums text-gray-900">{item.value}</p>
                  </div>
                ))}
                <div className="flex w-[150px] shrink-0 flex-col">
                  <div className="flex flex-1 flex-col justify-end">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      Exceptions
                    </p>
                  </div>
                  <p className="mt-0.5 font-semibold tabular-nums text-gray-900">
                    {exceptionCount > 0 ? exceptionCount : '—'}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <BankReconLinker
            detail={detail}
            canEdit={canEdit}
            onLink={handleLink}
            onIgnore={handleIgnore}
            onRemoveOverride={handleRemoveOverride}
            onUnlinkOverrides={handleUnlinkOverrides}
          />

          <BankReconExceptionsTable detail={detail} />

          {canEdit ? (
            <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="border-b border-gray-200 px-5 py-4">
                <h2 className="text-base font-semibold text-gray-900">Reconcile</h2>
                <p className="mt-1 text-xs text-gray-500">
                  Mark this account/month as reconciled in BookKeeper. Note: QBO is never updated from this page.
                </p>
              </div>
              <div className="space-y-3 px-5 py-4">
                <label className="block text-sm text-gray-700">
                  <span className="mb-1 block text-xs font-medium text-gray-500">Note</span>
                  <textarea
                    className="input w-full max-w-xl text-sm"
                    rows={5}
                    value={reconcileNote}
                    onChange={(event) => setReconcileNote(event.target.value)}
                    placeholder={
                      exceptionCount > 0
                        ? 'Required when exceptions remain'
                        : 'Optional note'
                    }
                  />
                </label>
                {isReconciled ? (
                  <ReconcileStatusCallout tone="success" title="Reconciled in BookKeeper">
                    {(detail.reconciled_by || detail.reconciled_at) ? (
                      <p>
                        {detail.reconciled_by ? `By ${detail.reconciled_by}` : null}
                        {detail.reconciled_by && detail.reconciled_at ? ' · ' : null}
                        {detail.reconciled_at ? formatLocalDateTime(detail.reconciled_at) : null}
                      </p>
                    ) : null}
                    {detail.reconcile_note ? <p>{detail.reconcile_note}</p> : null}
                  </ReconcileStatusCallout>
                ) : exceptionCount > 0 ? (
                  <ReconcileStatusCallout
                    tone="warning"
                    title={
                      exceptionCount === 1
                        ? '1 exception remains'
                        : `${exceptionCount} exceptions remain`
                    }
                  >
                    <p>Add a note to mark reconciled anyway.</p>
                  </ReconcileStatusCallout>
                ) : (
                  <ReconcileStatusCallout tone="ready" title="Ready to reconcile">
                    <p>No exceptions remain.</p>
                  </ReconcileStatusCallout>
                )}
                <div className="flex flex-wrap gap-2">
                  {!isReconciled ? (
                    <Button
                      type="button"
                      variant="primary"
                      gradient
                      disabled={markDisabled}
                      onClick={() => void handleMarkReconciled(true)}
                    >
                      Mark reconciled
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={markingReconciled}
                      onClick={() => void handleMarkReconciled(false)}
                    >
                      Mark un-reconciled
                    </Button>
                  )}
                </div>
              </div>
            </section>
          ) : detail.reconciled_at ? (
            <p className="text-sm text-gray-600">
              Reconciled in BookKeeper
              {detail.reconciled_by ? ` by ${detail.reconciled_by}` : ''}
              {detail.reconciled_at ? ` at ${formatLocalDateTime(detail.reconciled_at)}` : ''}
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  )
}
